import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/apiService';
import { useNotification } from './NotificationProvider';
import { FaCrown } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import confetti from 'canvas-confetti';
import { authService } from '../services/authService';

const PaymentReturn = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showError } = useNotification();
    const [isProcessing, setIsProcessing] = useState(true);
    const [showWelcome, setShowWelcome] = useState(false);
    const [returnUrl, setReturnUrl] = useState('');
    const [error, setError] = useState(null);
    const [hasAttemptedVerification, setHasAttemptedVerification] = useState(false);
    const [teamLogo, setTeamLogo] = useState(null);
    const [teamLogoLoaded, setTeamLogoLoaded] = useState(false);

    const handleClose = useCallback(() => {
        sessionStorage.removeItem('pro_return_url');
        navigate(returnUrl, { replace: true });
    }, [navigate, returnUrl]);

    // Function to load team logo (same logic as header)
    const loadTeamLogo = useCallback((teamId) => {
        if (teamId) {
            const logoUrl = `https://football.pebbledesigns.co.uk/api/images/teams/${teamId}/logo-light.png`;
            const img = new Image();
            img.onload = () => {
                setTeamLogo(logoUrl);
                setTeamLogoLoaded(true);
            };
            img.onerror = () => {
                setTeamLogo(null);
                setTeamLogoLoaded(false);
            };
            img.src = logoUrl;
        } else {
            setTeamLogo(null);
            setTeamLogoLoaded(false);
        }
    }, []);

    // Confetti effect function
    const triggerConfetti = () => {
        const duration = 2000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1500 };

        function randomInRange(min, max) {
            return Math.random() * (max - min) + min;
        }

        const interval = setInterval(() => {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                return clearInterval(interval);
            }

            const particleCount = 50 * (timeLeft / duration);
            
            // Create confetti bursts at various positions
            confetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.2, 0.4), y: randomInRange(0.2, 0.4) }
            });
            confetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.6, 0.8), y: randomInRange(0.2, 0.4) }
            });
        }, 250);
    };

    useEffect(() => {
        const handlePaymentReturn = async () => {
            if (hasAttemptedVerification) {
                return;
            }

            const sessionId = searchParams.get('session_id');
            const paymentType = searchParams.get('type');
            const phoneNumber = searchParams.get('phone');
            const userType = searchParams.get('userType');
            const returnPath = sessionStorage.getItem('pro_return_url') || '/dashboard';

            console.log('Payment return parameters:', {
                sessionId,
                paymentType,
                phoneNumber,
                userType,
                returnPath
            });

            // Check if required parameters are present
            if (!sessionId) {
                setError('Missing session_id in URL parameters');
                setIsProcessing(false);
                return;
            }

            setReturnUrl(returnPath);

            // Use polling to check if webhook has processed the payment
            const checkPaymentStatus = async () => {
                try {
                    const response = await api.get(`${process.env.REACT_APP_API_URL}/check_subscription_status.php?session_id=${sessionId}`);
                    
                    if (response.data.success) {
                        if (response.data.processed) {
                            // Payment has been processed by webhook
                            console.log('Payment processed by webhook:', response.data);
                            
                            // Refresh the JWT token to get updated pro status
                            // Only if user is logged in (has access token)
                            try {
                                const token = authService.getAccessToken();
                                if (token) {
                                    await authService.refreshTokenAfterPayment();
                                    console.log('Token refreshed successfully after payment');
                                } else {
                                    console.log('User not logged in - skipping token refresh');
                                    // If user came from Pro signup page, redirect them to login
                                    if (phoneNumber && userType) {
                                        // Store phone number for login
                                        sessionStorage.setItem('pro_signup_phone', phoneNumber);
                                        sessionStorage.setItem('pro_signup_userType', userType);
                                        // Update return path to login with phone pre-filled
                                        setTimeout(() => {
                                            navigate(`/?phone=${phoneNumber}&pro_signup=true&payment_success=true`, { replace: true });
                                        }, 3000);
                                        return; // Exit early to prevent default redirect
                                    }
                                }
                            } catch (error) {
                                console.error('Failed to refresh token after payment:', error);
                                // Continue anyway - the user can refresh manually if needed
                                // If user came from Pro signup and isn't logged in, redirect to login
                                if (phoneNumber && userType && !authService.getAccessToken()) {
                                    sessionStorage.setItem('pro_signup_phone', phoneNumber);
                                    sessionStorage.setItem('pro_signup_userType', userType);
                                    setTimeout(() => {
                                        navigate(`/?phone=${phoneNumber}&pro_signup=true&payment_success=true`, { replace: true });
                                    }, 3000);
                                    return;
                                }
                            }
                            
                            triggerConfetti();
                            setShowWelcome(true);
                            setIsProcessing(false);
                            setHasAttemptedVerification(true);
                            
                            // Redirect after a short delay
                            setTimeout(() => {
                                navigate(returnPath, { replace: true });
                            }, 3000);
                        } else {
                            // Still processing, check again in 2 seconds
                            console.log('Payment still processing:', response.data.message);
                            setTimeout(checkPaymentStatus, 2000);
                        }
                    } else {
                        throw new Error(response.data.error || 'Failed to check payment status');
                    }
                } catch (error) {
                    console.error('Error checking payment status:', error);
                    
                    // If it's been more than 30 seconds, show error
                    if (Date.now() - startTime > 30000) {
                        setError('Payment verification timed out. Please check your subscription status.');
                        setIsProcessing(false);
                        setHasAttemptedVerification(true);
                    } else {
                        // Retry after 3 seconds
                        setTimeout(checkPaymentStatus, 3000);
                    }
                }
            };

            const startTime = Date.now();
            checkPaymentStatus();
        };

        handlePaymentReturn();
    }, [searchParams, hasAttemptedVerification]);

    return (
        <div className="payment-return-container" onClick={handleClose}>
            <div className="payment-return-content" onClick={e => e.stopPropagation()}>
                {isProcessing ? (
                    <>
                        <h2>Processing your payment...</h2>
                        <p>Please wait while we verify your payment. This may take a few moments.</p>
                    </>
                ) : error ? (
                    <div className="error-message">
                        <h2>Payment Verification Error</h2>
                        <p>{error}</p>
                        <button className="close-button" onClick={handleClose}>
                            Return to Dashboard
                        </button>
                    </div>
                ) : showWelcome ? (
                    <div className="welcome-pro-message">
                        <button className="close-button" onClick={handleClose}>
                            <IoMdClose />
                        </button>
                        <FaCrown className="crown-icon" />
                        <h1>Welcome to FirstWhistle Pro!</h1>
                        {(sessionStorage.getItem('pro_team_name') || sessionStorage.getItem('selected_team_name')) && (
                            <div className="team-info">
                                {teamLogo && teamLogoLoaded && (
                                    <div className="team-logo-container">
                                        <img 
                                            src={teamLogo} 
                                            alt="Team Logo" 
                                            className="team-logo"
                                            onError={() => setTeamLogoLoaded(false)}
                                        />
                                    </div>
                                )}
                                <h2 className="team-name">
                                    {sessionStorage.getItem('pro_team_name') || sessionStorage.getItem('selected_team_name')}
                                </h2>
                            </div>
                        )}
                        <p className="personal-message">
                            Thank you for upgrading to FirstWhistle Pro. Your support helps us continue developing features that make grassroots football better for everyone.
                        </p>
                        <p className="personal-message">
                            You now have unlimited access to:
                        </p>
                        <div className="feature-list">
                            <ul>
                                <li>✨ Unlimited match history</li>
                                <li>📊 Advanced performance analytics</li>
                                <li>🏆 Detailed achievement tracking</li>
                                <li>📈 Team statistics and insights</li>
                                <li>🎯 Personal development tools</li>
                            </ul>
                        </div>
                        <div className="refresh-notice">
                            <p className="refresh-text">
                                If you don't see your Pro features immediately, you may need to refresh the page or log out and back in.
                            </p>
                            <button 
                                className="refresh-button" 
                                onClick={() => window.location.reload()}
                            >
                                Refresh Page
                            </button>
                        </div>
                    </div>
                ) : (
                    // Fallback content to prevent null render
                    <div className="processing-complete">
                        <h2>Processing Complete</h2>
                        <p>Redirecting to dashboard...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentReturn; 